import { Injectable } from '@angular/core';
import { User } from '@models/imported/SagaBase/User';
import { CommunicationEquipment } from '@models/imported/SagaSchema/CommunicationEquipment';
import { UnitActivity } from '@models/imported/SagaSchema/UnitActivity';
import { Vehicle } from '@models/imported/SagaSchema/Vehicle';
import { IUnitSetup, MyEquipmentService } from '@techwan/ionic-core';
import { UnitActivity as UA } from '@techwan/ionic-core/lib/schema/imported/SagaSchema/UnitActivity';
import { ILoginResult } from '@techwan/security';
import { filter } from 'rxjs/operators';
import { CacheService } from '../cache/cache.service';
import { FetchListService } from '../fetchlist/fetchlist.service';
import { Initializer } from '../initializer/initializer.service';
import { ObjectFactory } from '../object-factory/object-factory.service';
import { Security } from '../security/security.service';

export const DEVICE_SECURITY_KEY = 'saga_device_associate';

@Injectable()
export class AssociationSecurity {
  private _loginData: ILoginResult;

  private _device: CommunicationEquipment;
  get device(): CommunicationEquipment {
    return this._device;
  }

  private _unitActivity: UnitActivity;
  get unitActivity(): UnitActivity {
    return this._unitActivity;
  }
  set unitActivity(unitActivity: UnitActivity) {
    this._unitActivity = unitActivity;
    this.security.setData('UnitActivity', unitActivity);
  }

  /**
   * @deprecated Please consider using RefreshTokenService from LoginModule instead.
   */
  get loginData(): ILoginResult {
    return this._loginData;
  }

  /**
   * @deprecated Please consider using CredentialService from ionic-core instead.
   */
  set loginData(value: ILoginResult) {
    if (value.SagaUserAuth) {
      value.SagaUserAuth = this.cache.objectFactory.create(value.SagaUserAuth, 'SagaBase.User');
    }
    this.security.loggedUser = value.SagaUserAuth as User;
    this._loginData = value;
  }

  constructor(
    private security: Security,
    private _fetchList: FetchListService,
    initializer: Initializer,
    private cache: CacheService,
    private readonly myEquipmentService: MyEquipmentService,
    private readonly factory: ObjectFactory
  ) {
    initializer.$onComplete.pipe(filter(ready => ready)).subscribe(() => this.onInitializerReady());

    this.cache.listenForChange(UnitActivity).subscribe(event => this.onUnitActivityChanged(event.object as UnitActivity));
  }

  private onUnitActivityChanged(unitActivity: UnitActivity) {
    if (this.unitActivity && this.unitActivity.ObjGuid === unitActivity.ObjGuid && this.unitActivity !== unitActivity) {
      this.unitActivity = unitActivity;
    }
  }

  onInitializerReady(): void {
    let unit: UA = this.myEquipmentService.myUnit;
    this._device = this.factory.create(this.myEquipmentService.myDevice, CommunicationEquipment);

    if (unit !== null) {
      this.unitActivity = this.cache.getObjectByUid(unit.ObjGuid);

      if (this.unitActivity.vehicles.length === 0 && unit.resources && unit.resources.vehicles) {
        // unit resources are not booted
        unit.resources.vehicles.forEach(vehicleResource => {
          const vehicle: Vehicle = this.factory.create(vehicleResource);
          vehicle.update(this._fetchList);
          this.unitActivity.vehicles.push(vehicle);
        });
      }

      // Devices are not booted
      unit.resources.devices.forEach(device => this.unitActivity.devices.push(this.factory.create(device)));
    }
  }

  setDevice(name: string) {
    this.security.addSecurityData(DEVICE_SECURITY_KEY, name);
  }

  public async fetchUnitActivity(): Promise<any> {
    try {
      return await this.security
        .post('api/UnitActivity/GetUnitActivity', {
          equipmentUid: this.device.ObjGuid
        })
        .then(response => {
          if (response) {
            this.readUnitActivityResponse(response);
          } else {
            delete this.unitActivity;
          }

          return {
            success: true,
            activity: this.unitActivity
          };
        });
    } catch (response) {
      return {
        success: false,
        activity: null
      };
    }
  }

  private readUnitActivityResponse(data: IUnitSetup) {
    this._unitActivity = this.cache.getObjectByUid(data.unit.ObjGuid);

    if (!this._unitActivity) {
      this._unitActivity = this.cache.objectFactory.create(data.unit, UnitActivity);
      this._unitActivity.setListResourceStatus(this._fetchList);
    }

    this.setResources(this._unitActivity, data);
  }

  setResources(unitActivity: UnitActivity, resources: IUnitSetup) {
    unitActivity.devices.length = 0;
    unitActivity.vehicles.length = 0;

    resources.devices.forEach(com => {
      unitActivity.devices.push(this.cache.objectFactory.create(com));
    });

    resources.vehicles.forEach(vehicle => {
      const vehicles: Vehicle = this.cache.objectFactory.create(vehicle);
      vehicles.update(this._fetchList);
      unitActivity.vehicles.push(vehicles);
    });
  }
}
